import React, { useEffect, useState } from 'react';
import { logger } from '../../utils/logger';
import { PlusIcon, SearchIcon, EditIcon, UsersIcon, DownloadIcon, StethoscopeIcon, LockIcon, ArchiveIcon, PowerOffIcon, CheckCircleIcon, RotateCcwIcon, XCircleIcon } from 'lucide-react';
import { Card } from '../ui/Card';
import { Button } from '../ui/Button';
import { Input } from '../ui/Input';
import { Textarea } from '../ui/Textarea';
import { Modal } from '../ui/Modal';
import { useData } from '../../contexts/DataContext';
import { useAuth } from '../../contexts/AuthContext';
import { useToast } from '../../contexts/ToastContext';
import { usePermissions } from '../../hooks/usePermissions';
import { Specialty } from '../../types';
import { exportSpecialtiesToExcel } from '../../utils/excelExport';
import { exportSpecialtiesToPdf } from '../../utils/pdfExport';
import { usePagination } from '../../hooks/usePagination';
import { Pagination } from '../ui/Pagination';
import { ExcelExportModal } from '../ui/ExcelExportModal';
import { PdfExportModal } from '../ui/PdfExportModal';
import { useConfirm } from '../../contexts/ConfirmContext';
export function Specialties() {
  const {
    specialties,
    addSpecialty,
    updateSpecialty,
    deleteSpecialty,
    getDoctorsBySpecialty
  } = useData();
  const {
    user
  } = useAuth();
  const {
    showToast
  } = useToast();
  const {
    confirm
  } = useConfirm();
  const isAdmin = user?.role === 'administrador';
  const userRoleNorm = user?.role?.toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '') || '';
  const canExportExcel = isAdmin || user?.role === 'terapeuta' || user?.role === 'supervisor/a' || userRoleNorm === 'recepcion' || userRoleNorm === 'recepcionista';
  const canExportPdf = isAdmin || user?.role === 'terapeuta' || user?.role === 'supervisor/a' || userRoleNorm === 'recepcion' || userRoleNorm === 'recepcionista';
  const {
    canCreate,
    canEdit,
    canDelete,
    isReadOnly
  } = usePermissions();
  const readOnly = isReadOnly('specialties');
  const [searchTerm, setSearchTerm] = useState('');
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [selectedSpecialty, setSelectedSpecialty] = useState<Specialty | null>(null);
  const [isDeleteConfirmOpen, setIsDeleteConfirmOpen] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    description: ''
  });
  const [formErrors, setFormErrors] = useState<Record<string, string>>({});
  const [isExporting, setIsExporting] = useState(false);
  const [isExportModalOpen, setIsExportModalOpen] = useState(false);
  const [isPdfExporting, setIsPdfExporting] = useState(false);
  const [isPdfExportModalOpen, setIsPdfExportModalOpen] = useState(false);
  // Archive/Deactivate Logic
  const STORAGE_KEY = 'savidna-specialties-status';
  const [itemStatuses, setItemStatuses] = useState<Record<string, 'archived' | 'deactivated'>>({});
  const [viewMode, setViewMode] = useState<'active' | 'archived' | 'deactivated'>('active');
  const [isActionConfirmOpen, setIsActionConfirmOpen] = useState(false);
  const [actionType, setActionType] = useState<'archive' | 'deactivate' | null>(null);
  useEffect(() => {
    try {
      const saved = localStorage.getItem(STORAGE_KEY);
      if (saved) setItemStatuses(JSON.parse(saved));
    } catch {}
  }, []);
  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(itemStatuses));
  }, [itemStatuses]);
  const getItemStatus = (id: string): 'active' | 'archived' | 'deactivated' => itemStatuses[id] || 'active';
  const archiveItem = (id: string) => {
    setItemStatuses(prev => ({
      ...prev,
      [id]: 'archived'
    }));
    updateSpecialty(id, {
      status: 'archived'
    } as any).catch(e => logger.error('Error archiving specialty in Supabase:', e));
    setIsActionConfirmOpen(false);
  };
  const deactivateItem = (id: string) => {
    setItemStatuses(prev => ({
      ...prev,
      [id]: 'deactivated'
    }));
    updateSpecialty(id, {
      status: 'deactivated'
    } as any).catch(e => logger.error('Error deactivating specialty in Supabase:', e));
    setIsActionConfirmOpen(false);
  };
  const restoreItem = (id: string) => {
    setItemStatuses(prev => {
      const next = {
        ...prev
      };
      delete next[id];
      return next;
    });
    updateSpecialty(id, {
      status: 'active'
    } as any).catch(e => logger.error('Error restoring specialty in Supabase:', e));
    setIsActionConfirmOpen(false);
  };
  const openActionConfirm = (specialty: Specialty, type: 'archive' | 'deactivate') => {
    setSelectedSpecialty(specialty);
    setActionType(type);
    setIsActionConfirmOpen(true);
  };
  const handleAction = () => {
    if (!selectedSpecialty || !actionType) return;
    if (actionType === 'archive') {
      archiveItem(selectedSpecialty.id);
      showToast({
        type: 'info',
        title: 'Especialidad archivada',
        message: `${selectedSpecialty.name} fue archivada.`
      });
    } else if (actionType === 'deactivate') {
      deactivateItem(selectedSpecialty.id);
      showToast({
        type: 'warning',
        title: 'Especialidad desactivada',
        message: `${selectedSpecialty.name} fue desactivada.`
      });
    }
  };
  const filteredSpecialties = specialties.filter(s => {
    const matchesSearch = s.name.toLowerCase().includes(searchTerm.toLowerCase());
    const status = getItemStatus(s.id);
    return matchesSearch && status === viewMode;
  });
  const {
    paginatedItems: paginatedSpecialties,
    currentPage,
    totalPages,
    totalItems,
    startIndex,
    endIndex,
    goToPage,
    nextPage,
    prevPage
  } = usePagination(filteredSpecialties);
  // Counts for tabs
  const activeCount = specialties.filter(s => getItemStatus(s.id) === 'active').length;
  const archivedCount = specialties.filter(s => getItemStatus(s.id) === 'archived').length;
  const deactivatedCount = specialties.filter(s => getItemStatus(s.id) === 'deactivated').length;
  const openNewForm = () => {
    if (!canCreate('specialties')) return;
    setSelectedSpecialty(null);
    setFormData({
      name: '',
      description: ''
    });
    setFormErrors({});
    setIsFormOpen(true);
  };
  const openEditForm = (specialty: Specialty) => {
    if (!canEdit('specialties')) return;
    setSelectedSpecialty(specialty);
    setFormData({
      name: specialty.name,
      description: specialty.description
    });
    setFormErrors({});
    setIsFormOpen(true);
  };
  const openDeleteConfirm = (specialty: Specialty) => {
    if (!canDelete('specialties')) return;
    setSelectedSpecialty(specialty);
    setIsDeleteConfirmOpen(true);
  };
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const errors: Record<string, string> = {};
    if (!formData.name) errors.name = 'El nombre es requerido';
    if (Object.keys(errors).length > 0) {
      setFormErrors(errors);
      return;
    }
    confirm({
      title: selectedSpecialty ? 'Guardar Cambios' : 'Crear Especialidad',
      message: selectedSpecialty ? '¿Estás seguro de que deseas guardar los cambios de esta especialidad?' : '¿Estás seguro de que deseas registrar esta nueva especialidad?',
      confirmText: 'Guardar',
      onConfirm: () => {
        if (selectedSpecialty) {
          updateSpecialty(selectedSpecialty.id, formData);
          showToast({
            type: 'success',
            title: 'Especialidad actualizada',
            message: `${formData.name} fue actualizada correctamente.`
          });
        } else {
          addSpecialty(formData);
          showToast({
            type: 'success',
            title: 'Especialidad creada',
            message: `${formData.name} fue registrada correctamente.`
          });
        }
        setIsFormOpen(false);
      }
    });
  };
  const handleDelete = () => {
    if (!canDelete('specialties')) return;
    if (selectedSpecialty) {
      const name = selectedSpecialty.name;
      deleteSpecialty(selectedSpecialty.id);
      showToast({
        type: 'error',
        title: 'Especialidad eliminada',
        message: `${name} fue eliminada permanentemente.`
      });
      setIsDeleteConfirmOpen(false);
      setSelectedSpecialty(null);
    }
  };
  const handleExportToExcel = async (dateFrom: string | null, dateTo: string | null) => {
    setIsExporting(true);
    await new Promise(resolve => setTimeout(resolve, 500));
    exportSpecialtiesToExcel(specialties);
    setIsExporting(false);
    setIsExportModalOpen(false);
  };
  const handleExportToPdf = async (dateFrom: string | null, dateTo: string | null) => {
    setIsPdfExporting(true);
    await new Promise(resolve => setTimeout(resolve, 500));
    exportSpecialtiesToPdf(specialties);
    setIsPdfExporting(false);
    setIsPdfExportModalOpen(false);
  };
  return <div className="space-y-6" data-id="element-6071" data-mp="3qv4s727xlwvz">
      {/* Header Banner */}
      <div className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-rose-600 via-rose-500 to-pink-500 shadow-xl text-white" data-id="element-6072" data-mp="1dfms7q74qasv">
        {/* Decorative pattern */}
        <div className="absolute inset-0 opacity-[0.06] pointer-events-none" style={{
        backgroundImage: 'repeating-linear-gradient(45deg, transparent, transparent 13px, rgba(255,255,255,1) 13px, rgba(255,255,255,1) 14px), repeating-linear-gradient(-45deg, transparent, transparent 13px, rgba(255,255,255,1) 13px, rgba(255,255,255,1) 14px)'
      }} data-id="element-6073" data-mp="1merriyqim863" />
        {/* Glow */}
        <div className="absolute -top-8 -right-8 w-48 h-48 rounded-full blur-2xl opacity-20 pointer-events-none bg-white" data-id="element-6074" data-mp="264jzyzdpsp7d" />
        {/* Left accent strip */}
        <div className="absolute left-0 top-0 bottom-0 w-1.5 rounded-l-2xl bg-white/30" data-id="element-6075" data-mp="2f474vv5nkd56" />
        <div className="absolute -top-10 -right-10 w-72 h-72 rounded-full opacity-20 pointer-events-none" style={{
        background: 'radial-gradient(circle, rgba(255,255,255,0.6) 0%, transparent 65%)'
      }} data-id="element-6076" data-mp="2k31mitrvvvnp" />
        <div className="absolute -bottom-16 -left-8 w-64 h-64 rounded-full opacity-15 pointer-events-none" style={{
        background: 'radial-gradient(circle, rgba(244,63,94,0.8) 0%, transparent 60%)'
      }} data-id="element-6077" data-mp="kdvaj6whxy5h" />
        <div className="absolute left-0 top-0 bottom-0 w-1.5 rounded-l-2xl bg-white/25" data-id="element-6078" data-mp="y5tel3bcp3us" />
        <div className="relative z-10 p-6 pl-7 flex flex-col md:flex-row md:items-center md:justify-between gap-4" data-id="element-6079" data-mp="133bnitpdbj2s">
          <div data-id="element-6080" data-mp="184910szfnoxy">
            <div className="flex items-center gap-3 mb-2" data-id="element-6081" data-mp="183pk9flir6li">
              <div className="p-2.5 rounded-xl bg-white/15 border border-white/20 backdrop-blur-sm" data-id="element-6082" data-mp="1c5b4jm0dun5q">
                <StethoscopeIcon className="w-6 h-6 text-white" data-id="element-6083" data-mp="39vcfxugcvpob" />
              </div>
              <h1 className="text-2xl font-bold text-white" data-id="element-6084" data-mp="3dvw9yp5mud68">Especialidades</h1>
            </div>
            <p className="max-w-xl text-sm font-medium text-white/80" data-id="element-6085" data-mp="36cdc5crqcfdl">
              Gestión de especialidades médicas
              {readOnly && <span className="ml-2 bg-white/20 px-2 py-0.5 rounded text-xs font-medium" data-id="element-6086" data-mp="31kdiu3660kpz">
                  Solo lectura
                </span>}
            </p>
          </div>
          <div className="flex gap-3" data-id="element-6087" data-mp="2smjjskh0ncz4">
            {canExportPdf && <Button variant="outline" leftIcon={<StethoscopeIcon className="w-4 h-4" data-id="element-6089" data-mp="3p9503zi7vinz" />} onClick={() => setIsPdfExportModalOpen(true)} className="bg-white/10 border-white/30 text-white hover:bg-white/20 backdrop-blur-sm" data-id="element-6088" data-mp="3pbra05skya9d">
                Exportar PDF
              </Button>}
            {canExportExcel && <Button variant="outline" leftIcon={<DownloadIcon className="w-4 h-4" data-id="element-6091" data-mp="1zwks0hbft1qr" />} onClick={() => setIsExportModalOpen(true)} className="bg-white/10 border-white/30 text-white hover:bg-white/20 backdrop-blur-sm" data-id="element-6090" data-mp="24vxpdvzxpr7l">
                Exportar Excel
              </Button>}
            {canCreate('specialties') && <Button leftIcon={<PlusIcon className="w-4 h-4" data-id="element-6093" data-mp="1scs8htjiviy8" />} onClick={openNewForm} className="bg-white text-rose-700 hover:bg-rose-50 shadow-lg shadow-rose-900/20" data-id="element-6092" data-mp="1sc0h6uwyk7dr">
                Nueva Especialidad
              </Button>}
          </div>
        </div>
      </div>

      {/* Filters & Search */}
      <div className="flex flex-col md:flex-row gap-4 justify-between items-end" data-id="element-6094" data-mp="18pkx84loetod">
        <div className="w-full md:w-96 relative" data-id="element-6095" data-mp="1do5mxa3axuyk">
          <SearchIcon className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" data-id="element-6096" data-mp="1doxjoo8mxyqf" />
          <Input style={{
          paddingLeft: '2.5rem'
        }} placeholder="Buscar especialidades..." value={searchTerm} onChange={e => setSearchTerm(e.target.value)} className="bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700" data-id="element-6097" data-mp="zp662om16kcy" />
        </div>

        {/* View Mode Tabs */}
        <div className="flex items-center gap-1 p-1 bg-gray-100 dark:bg-gray-800 rounded-xl w-full md:w-auto overflow-x-auto" data-id="element-6098" data-mp="u35dkxir979y">
          {[{
          key: 'active',
          label: 'Activos',
          count: activeCount,
          icon: CheckCircleIcon
        }, {
          key: 'archived',
          label: 'Archivados',
          count: archivedCount,
          icon: ArchiveIcon
        }, {
          key: 'deactivated',
          label: 'Desactivados',
          count: deactivatedCount,
          icon: XCircleIcon
        }].map(tab => <button key={tab.key} onClick={() => setViewMode(tab.key as any)} className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all whitespace-nowrap ${viewMode === tab.key ? 'bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow-sm' : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'}`} data-id="element-6099" data-mp="1hsghuozbdjsu">
              <tab.icon className="w-4 h-4" data-id="element-6100" data-mp="3pyigfu1nc301" />
              {tab.label}
              <span className="text-xs bg-gray-200 dark:bg-gray-600 px-1.5 py-0.5 rounded-full" data-id="element-6101" data-mp="2t9u9ptvds25x">
                {tab.count}
              </span>
            </button>)}
        </div>
      </div>

      {readOnly && <div className="p-3 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg text-amber-700 dark:text-amber-300 text-sm flex items-center gap-2" data-id="element-6102" data-mp="328xwb7trazp6">
          <LockIcon className="w-4 h-4" data-id="element-6103" data-mp="hkjhqknai3iu" />
          <span data-id="element-6104" data-mp="hlumamok7fb0">
            Solo tienes permisos de lectura. No puedes crear, editar o eliminar
            especialidades.
          </span>
        </div>}

      {/* Specialties Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-id="element-6105" data-mp="wm02vmpsojcp">
        {paginatedSpecialties.length === 0 ? <div className="col-span-full text-center py-12 text-gray-500 dark:text-gray-400" data-id="element-6106" data-mp="mwyq05ejqi2y">
            <div className="flex flex-col items-center justify-center" data-id="element-6107" data-mp="mum118adf7ge">
              <div className="w-16 h-16 bg-gray-100 dark:bg-gray-800 rounded-full flex items-center justify-center mb-4" data-id="element-6108" data-mp="iv4s24vqjs6h">
                <StethoscopeIcon className="w-8 h-8 text-gray-400" data-id="element-6109" data-mp="dtxlg95bwz1e" />
              </div>
              <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-1" data-id="element-6110" data-mp="8za6b0w7tgzh">
                No se encontraron especialidades
              </h3>
              <p className="text-sm" data-id="element-6111" data-mp="3w2yeqtwqcssk">
                {searchTerm ? 'Intenta con otros términos de búsqueda' : viewMode === 'active' ? 'Comienza registrando una nueva especialidad' : 'No hay especialidades en esta vista'}
              </p>
            </div>
          </div> : paginatedSpecialties.map(specialty => {
        const doctorCount = getDoctorsBySpecialty(specialty.id).length;
        return <div key={specialty.id} className="group relative flex flex-col bg-white dark:bg-gray-800 rounded-2xl ring-1 ring-gray-200/80 dark:ring-gray-700/60 shadow-sm hover:shadow-lg hover:-translate-y-0.5 transition-all duration-200 overflow-hidden" data-id="element-6112" data-mp="3etvrtu4iu4wm">
                {/* Top accent */}
                <div className="h-1 bg-gradient-to-r from-rose-500 to-pink-400" data-id="element-6113" data-mp="fb38aabbrzum" />
                {/* Card body */}
                <div className="flex items-start gap-4 p-5" data-id="element-6114" data-mp="6civfpsg4keb">
                  {/* Initial circle */}
                  <div className="flex-shrink-0 w-11 h-11 rounded-xl flex items-center justify-center text-lg font-bold text-white shadow-sm" style={{
              background: 'linear-gradient(135deg,#e11d48,#f43f5e)'
            }} data-id="element-6115" data-mp="2m46q1ejzm0ty">
                    {specialty.name.charAt(0).toUpperCase()}
                  </div>
                  {/* Content */}
                  <div className="flex-1 min-w-0" data-id="element-6116" data-mp="2sj44s9qmonrd">
                    <h3 className="font-bold text-gray-900 dark:text-white text-base leading-snug" data-id="element-6117" data-mp="2nl2q0108jalp">
                      {specialty.name}
                    </h3>
                    {specialty.description ? <p className="text-sm text-gray-500 dark:text-gray-400 mt-1 line-clamp-2" data-id="element-6118" data-mp="1uworlk58ic9m">
                        {specialty.description}
                      </p> : <p className="text-sm text-gray-400 dark:text-gray-600 italic mt-1" data-id="element-6119" data-mp="24uurjw7hd8bu">
                        Sin descripción
                      </p>}
                  </div>
                </div>
                {/* Footer */}
                <div className="px-5 pb-4 mt-auto flex items-center justify-between border-t border-gray-100 dark:border-gray-700/60 pt-3" data-id="element-6120" data-mp="1c5e0b1vljxh7">
                  <span className="inline-flex items-center gap-1.5 text-xs font-medium text-rose-600 dark:text-rose-400 bg-rose-50 dark:bg-rose-900/20 px-2.5 py-1 rounded-full" data-id="element-6121" data-mp="vlp2tu3jbo7k">
                    <UsersIcon className="w-3 h-3" data-id="element-6122" data-mp="10n4s5ycqwgvr" />
                    {doctorCount} {doctorCount === 1 ? 'doctor' : 'doctores'}
                  </span>
                  {/* Action buttons on hover */}
                  <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-all duration-150" data-id="element-6123" data-mp="19jm2x0h76cqz">
                    {!readOnly && viewMode === 'active' ? <>
                        {canEdit('specialties') && <button onClick={() => openEditForm(specialty)} className="p-1.5 text-gray-400 hover:text-rose-600 rounded-lg transition-colors" title="Editar" data-id="element-6124" data-mp="1o91hli3gerqb">
                            <EditIcon className="w-4 h-4" data-id="element-6125" data-mp="3htbt2z5ez8a9" />
                          </button>}
                        {canDelete('specialties') && <>
                            <button onClick={() => openActionConfirm(specialty, 'archive')} className="p-1.5 text-gray-400 hover:text-amber-500 rounded-lg transition-colors" title="Archivar" data-id="element-6126" data-mp="38tge0j8qmt4y">
                              <ArchiveIcon className="w-4 h-4" data-id="element-6127" data-mp="3rqzn1mv74brd" />
                            </button>
                            <button onClick={() => openActionConfirm(specialty, 'deactivate')} className="p-1.5 text-gray-400 hover:text-red-500 rounded-lg transition-colors" title="Desactivar" data-id="element-6128" data-mp="3msogbl076hdd">
                              <PowerOffIcon className="w-4 h-4" data-id="element-6129" data-mp="vzla243hyro6" />
                            </button>
                          </>}
                      </> : viewMode !== 'active' ? <button onClick={() => restoreItem(specialty.id)} className="p-1.5 text-gray-400 hover:text-green-600 rounded-lg transition-colors" title="Restaurar" data-id="element-6130" data-mp="mzos0lbm4r23">
                        <RotateCcwIcon className="w-4 h-4" data-id="element-6131" data-mp="19zcvgpwcfac7" />
                      </button> : null}
                  </div>
                </div>
              </div>;
      })}
      </div>

      {/* Pagination */}
      <Pagination currentPage={currentPage} totalPages={totalPages} totalItems={totalItems} startIndex={startIndex} endIndex={endIndex} onPageChange={goToPage} onNext={nextPage} onPrev={prevPage} data-id="element-6132" data-mp="22ggvm1eqnq56" />

      {/* Form Modal */}
      <Modal isOpen={isFormOpen} onClose={() => setIsFormOpen(false)} title={selectedSpecialty ? 'Editar Especialidad' : 'Nueva Especialidad'} icon={<StethoscopeIcon className="w-5 h-5" data-id="element-6134" data-mp="3ubzow2wnvond" />} accentColor="rose" data-id="element-6133" data-mp="3lbtdcmkiwyqu">
        <form onSubmit={handleSubmit} className="space-y-6" data-id="element-6135" data-mp="356noz1vbpiu">
          {/* Specialty Info */}
          <div data-id="element-6136" data-mp="849vusd5abv1">
            <div className="flex items-center gap-2 mb-4" data-id="element-6137" data-mp="83qbz63xpded">
              <span className="w-1.5 h-5 rounded-full bg-rose-500" data-id="element-6138" data-mp="825gne9spqfg"></span>
              <h3 className="text-sm font-semibold text-gray-900 dark:text-white uppercase tracking-wide" data-id="element-6139" data-mp="cw9odzqvy2hl">
                Información de la Especialidad
              </h3>
            </div>
            <div className="bg-gray-50 dark:bg-gray-900/30 rounded-xl p-4 space-y-4 border border-gray-100 dark:border-gray-700/50" data-id="element-6140" data-mp="lwppx3o2izjt">
              <Input label="Nombre de la Especialidad" value={formData.name} onChange={e => setFormData({
              ...formData,
              name: e.target.value
            })} error={formErrors.name} placeholder="Ej: Cardiología, Pediatría..." data-id="element-6141" data-mp="qsn7teezcdyp" />
              <Textarea label="Descripción" value={formData.description} onChange={e => setFormData({
              ...formData,
              description: e.target.value
            })} rows={3} placeholder="Descripción de la especialidad..." data-id="element-6142" data-mp="2ruw6hf5gehwf" />
            </div>
          </div>

          {/* Footer */}
          <div className="flex justify-end gap-3 pt-4 border-t border-gray-100 dark:border-gray-700" data-id="element-6143" data-mp="1l9lif062m851">
            <Button variant="outline" type="button" onClick={() => setIsFormOpen(false)} data-id="element-6144" data-mp="1gah8u8f8i8e6">
              Cancelar
            </Button>
            <Button type="submit" data-id="element-6145" data-mp="qrgxpr0jros">
              {selectedSpecialty ? 'Guardar Cambios' : 'Crear Especialidad'}
            </Button>
          </div>
        </form>
      </Modal>

      {/* Action Confirmation Modal */}
      <Modal isOpen={isActionConfirmOpen} onClose={() => setIsActionConfirmOpen(false)} title={actionType === 'archive' ? 'Archivar Especialidad' : 'Desactivar Especialidad'} size="sm" data-id="element-6146" data-mp="3n4rn4xg53wuf">
        <div className="space-y-4" data-id="element-6147" data-mp="2z8n8fevnv2c6">
          <p className="text-gray-600 dark:text-gray-400" data-id="element-6148" data-mp="2z9ol1nsbrww0">
            {actionType === 'archive' && `¿Estás seguro de que deseas archivar a "${selectedSpecialty?.name}"? Podrás restaurarlo más tarde.`}
            {actionType === 'deactivate' && `¿Estás seguro de que deseas desactivar a "${selectedSpecialty?.name}"? No aparecerá en las listas activas.`}
          </p>
          <div className="flex justify-end gap-3" data-id="element-6149" data-mp="2yigessm1ze2o">
            <Button variant="outline" onClick={() => setIsActionConfirmOpen(false)} data-id="element-6150" data-mp="2uho6q2ox3f7z">
              Cancelar
            </Button>
            <Button variant="primary" onClick={handleAction} data-id="element-6151" data-mp="2pkehoo5fx5ko">
              {actionType === 'archive' ? 'Archivar' : 'Desactivar'}
            </Button>
          </div>
        </div>
      </Modal>

      {/* Excel Export Modal */}
      <ExcelExportModal isOpen={isExportModalOpen} onClose={() => setIsExportModalOpen(false)} onExport={handleExportToExcel} title="Exportar Especialidades" isExporting={isExporting} data-id="element-6152" data-mp="2atns7mrbmikq" />

      {/* PDF Export Modal */}
      <PdfExportModal isOpen={isPdfExportModalOpen} onClose={() => setIsPdfExportModalOpen(false)} onExport={handleExportToPdf} title="Exportar Especialidades a PDF" isExporting={isPdfExporting} data-id="element-6153" data-mp="21uk4u6l4fh0f" />
    </div>;
}